// createMapView(): the three.js map behind the MapView interface (src/view/api.ts).
//
//   Engine (renderer, loop, post) ─┬─ CivCamera (springs, input, wrap)
//                                  ├─ LightRig + Atmosphere (painted light, haze, background)
//                                  ├─ TerrainChunks ◄── MeshClient ◄── mesh worker (terrain.ts port)
//                                  │     └─ terrain material ◄── SurfaceChain (hex edges, lakes, rivers, …)
//                                  ├─ Ocean plane (bathymetry, waves, foam, sea ice)
//                                  ├─ Globe (the flat world band baked to a texture, curled at world zoom)
//                                  └─ HexData (per-hex textures) ◄── setSnapshot / setLens / highlight
//
// Later modules (borders, lens, fog, features, post effects) plug into `view.core` after mount().

import type UniformNode from 'three/src/nodes/core/UniformNode.js';
import { uniform } from 'three/tsl';
import { HEX_COUNT, hexAtLonLat, hexCenter, hexCol, hexRow, lonLatToWorld } from '../core/grid.ts';
import type { LensResult, WorldSnapshot } from '../world/api.ts';
import type { HexPick, MapView, ViewMode, ViewStats } from './api.ts';
import { CivCamera, GLOBE, globeAmount, hazeScale } from './camera.ts';
import type { CameraPose } from './camera.ts';
import { Engine, type QualityTier } from './engine.ts';
import { HexData, type PhysicalGrid } from './hexdata.ts';
import { citySignature, placeCities } from './features/cities.ts';
import { FeatureLayer } from './features/layer.ts';
import { createGlobe, type Globe } from './globe.ts';
import { createAtmosphere, createLights, type LightRig } from './lighting.ts';
import { hexEdgeLayer, highlightLayer, hoverLayer } from './overlays.ts';
import { HoverTracker, Picker } from './picking.ts';
import { borderLayer } from './political/borders.ts';
import { installPost } from './post.ts';
import { LabelLayer, type ActorLabel, type LabelCounts } from './political/labels.ts';
import { SurfaceChain } from './surface.ts';
import { MeshClient, TerrainChunks } from './terrain/chunks.ts';
import { hexTopHeight, shapeHexes, type HexShapes } from './terrain/heights.ts';
import { createTerrainMaterial } from './terrain/material.ts';
import { loadTerrainTextures } from './terrain/textures.ts';
import { createOcean, lakeLayer, riverLayer, smallLakeLayer, type Ocean } from './water.ts';
import { lakeShapes, type SmallLake } from './smalllakes.ts';
import { MODULES } from './modules.ts';

export type { QualityTier } from './engine.ts';
export type { PhysicalGrid } from './hexdata.ts';
export type { SmallLake } from './smalllakes.ts';
export type { ActorLabel, LabelCounts } from './political/labels.ts';

export interface MapViewOptions {
  physical: PhysicalGrid;
  tier?: QualityTier;
  /** 'webgl2' forces the WebGL 2 backend; 'auto' prefers WebGPU. */
  backend?: 'auto' | 'webgl2';
  /** Initial camera target and height (hex radii). */
  start?: { lon: number; lat: number; height: number };
  /** URL of the static render assets (public/assets): models and terrain textures. */
  assetBase?: string;
  /** Lakes smaller than a hex, drawn as water decals (never as hexes). */
  smallLakes?: readonly SmallLake[];
}

/** Everything a view module needs to plug in (available after mount). */
export interface ViewCore {
  engine: Engine;
  camera: CivCamera;
  data: HexData;
  surface: SurfaceChain;
  terrain: TerrainChunks;
  features: FeatureLayer;
  labels: LabelLayer;
  lights: LightRig;
  shapes: HexShapes;
  /** Seconds since start, as a shader uniform. */
  time: UniformNode<'float', number>;
  /** Camera height above the ground (hex radii), as a shader uniform. */
  camHeight: UniformNode<'float', number>;
}

/** Camera pose for shots: lon/lat target, height in hex radii, angles in degrees. */
export interface ViewPose {
  lon?: number;
  lat?: number;
  height?: number;
  /** Degrees from horizontal; null returns to the automatic pitch. */
  pitch?: number | null;
  yaw?: number;
}

export interface CoreMapView extends MapView {
  /** Modules plug in here; throws before mount() resolves. */
  readonly core: ViewCore;
  /** Jump the camera (no animation). */
  setPose(pose: ViewPose): void;
  /** Resolves when the camera is at rest and every visible chunk has its wanted LOD. */
  settle(timeoutMs?: number): Promise<void>;
  setTier(tier: QualityTier): void;
  /** Names, colours and sources per actor index: border colours, banners and country labels. */
  setActors(actors: readonly ActorLabel[]): void;
  /** Markers drawn this frame (banners, labels) and how many lack a source id. */
  markers(): LabelCounts;
}

const DEFAULT_START = { lon: 15, lat: 20, height: 700 };
const DEFAULT_ASSETS = '/assets';
const SETTLE_FRAMES = 3;
const SETTLE_TIMEOUT_MS = 60000;

// Warm-up view: close over the Alps and the Po plain, where terrain, conifers, broadleaf woods,
// peaks, lakes, rivers, cities and borders all show. Every prop kind is forced visible there too.
const WARM_POSE = { lon: 9.5, lat: 46.2, height: 14, pitch: 50 };
const WARM_FRAMES = 3;
const WARM_TIMEOUT_MS = 20000;
const CLICK_SLOP_PX = 5;

/** Labels and banners are placed on the flat map: hidden once the map starts to curl. */
const LABELS_OFF_MORPH = 0.01;

export function createMapView(opts: MapViewOptions): CoreMapView {
  let core: ViewCore | null = null;
  let mode: ViewMode = '3d';
  let picker: Picker | null = null;
  let ocean: Ocean | null = null;
  let globe: Globe | null = null;
  let labelsShown = true;
  let detach: (() => void) | null = null;
  const hoverCbs = new Set<(pick: HexPick | null) => void>();
  const clickCbs = new Set<(pick: HexPick) => void>();
  const hover = hoverLayer();
  let hoverHex = -1;
  const tracker = new HoverTracker();
  let cityKey = '';

  const need = (): ViewCore => {
    if (!core) {
      throw new Error('MapView: call mount() first');
    }
    return core;
  };

  const pick = (clientX: number, clientY: number): HexPick | null => {
    const c = need();
    const hex = hexUnder(c, picker, clientX, clientY);
    if (hex < 0) {
      return null;
    }
    const r = c.engine.renderer.domElement.getBoundingClientRect();
    return { hex, x: clientX - r.left, y: clientY - r.top };
  };

  // Runs every frame: a pointer move, or a camera move under a still pointer, re-picks.
  const updateHover = (pose: CameraPose): void => {
    if (!tracker.frame(poseKey(pose), performance.now())) {
      return;
    }
    const pointer = tracker.at();
    const p = pointer ? pick(pointer.x, pointer.y) : null;
    const hex = p ? p.hex : -1;
    if (hex === hoverHex) {
      return;
    }
    hoverHex = hex;
    hover.set(hex >= 0 ? { col: hexCol(hex), row: hexRow(hex) } : null);
    globe?.invalidate();
    for (const cb of hoverCbs) {
      cb(p);
    }
  };

  const view: CoreMapView = {
    get core() {
      return need();
    },

    async mount(container) {
      const camera = new CivCamera();
      const engine = await Engine.create(container, camera.camera, { tier: opts.tier, backend: opts.backend });
      const data = new HexData();
      data.setPhysical(opts.physical);
      const shapes = shapeHexes(opts.physical);
      data.setShapes(shapes);
      const assetBase = opts.assetBase ?? DEFAULT_ASSETS;
      const time = uniform(0);
      const camHeight = uniform(DEFAULT_START.height);

      const post = installPost(engine);
      const lights = createLights(engine.scene);
      const tier = engine.settings();
      lights.setShadows(tier.shadows, tier.shadowMapSize);
      const atmosphere = createAtmosphere(engine.scene);

      const surface = new SurfaceChain();
      surface.register(hexEdgeLayer());
      surface.register(lakeLayer());
      surface.register(smallLakeLayer(opts.smallLakes ?? []));
      surface.register(riverLayer());
      surface.register(borderLayer());
      surface.register(highlightLayer());
      surface.register(hover.layer);
      const features = new FeatureLayer({ data, time, assetBase, surface, camHeight });
      const [textures] = await Promise.all([loadTerrainTextures(assetBase), features.load()]);
      const material = createTerrainMaterial({ data, surface, style: lights.style, textures, time, camHeight });

      const terrain = new TerrainChunks(new MeshClient(opts.physical, lakeShapes(opts.smallLakes ?? []).map((s) => ({ x: s.x, z: s.z, r: s.reachUnits }))), material);
      terrain.setShadows(tier.shadows);
      features.setShadows(tier.shadows);
      engine.scene.add(terrain.group, features.group);
      ocean = createOcean({ data, time, camHeight });
      engine.scene.add(ocean.mesh);
      globe = createGlobe({ engine, camera, terrain, camHeight });

      const heightOf = (hex: number): number => hexTopHeight(shapes, hex);
      const labels = new LabelLayer(container, camera.camera, heightOf);

      const start = opts.start ?? DEFAULT_START;
      const p = lonLatToWorld(start.lon, start.lat);
      camera.jump({ x: p.x, z: p.z, height: start.height });
      core = { engine, camera, data, surface, terrain, features, labels, lights, shapes, time, camHeight };
      await terrain.preload(p.x, p.z);

      const dom = engine.renderer.domElement;
      picker = new Picker(camera.camera, dom, heightOf);
      engine.onFrame((dt, t) => {
        camera.update(dt);
        const pose = camera.pose();
        time.value = t;
        camHeight.value = pose.height;
        lights.follow(pose.x, pose.z, pose.height);
        atmosphere.update(pose.height * hazeScale(pose.pitch), pose.pitch);
        post.update(pose.height, pose.height / Math.sin((pose.pitch * Math.PI) / 180));
        terrain.update(camera.camera, pose.x, pose.z, pose.height);
        features.update(camera.camera, terrain.shown(), pose.x, pose.z, pose.height);
        const morph = globeAmount(pose.height);
        atmosphere.setSpace(morph);
        globe?.update(morph, t);
        const flat = morph <= LABELS_OFF_MORPH;
        if (flat !== labelsShown) {
          labelsShown = flat;
          labels.root.style.display = flat ? '' : 'none';
        }
        if (flat) {
          labels.update(pose.x, pose.height);
        }
        updateHover(pose);
      });

      const detachCamera = camera.attach(dom);
      let down: { x: number; y: number } | null = null;
      const onMove = (e: PointerEvent): void => {
        tracker.moved(e.clientX, e.clientY);
      };
      const onLeave = (): void => {
        tracker.left();
      };
      const onDown = (e: PointerEvent): void => {
        down = { x: e.clientX, y: e.clientY };
      };
      const onUp = (e: PointerEvent): void => {
        if (!down || camera.dragging() || Math.hypot(e.clientX - down.x, e.clientY - down.y) > CLICK_SLOP_PX) {
          down = null;
          return;
        }
        down = null;
        const hit = pick(e.clientX, e.clientY);
        if (!hit) {
          return;
        }
        for (const cb of clickCbs) {
          cb(hit);
        }
      };
      dom.addEventListener('pointermove', onMove);
      dom.addEventListener('pointerleave', onLeave);
      dom.addEventListener('pointerdown', onDown);
      dom.addEventListener('pointerup', onUp);
      detach = () => {
        detachCamera();
        dom.removeEventListener('pointermove', onMove);
        dom.removeEventListener('pointerleave', onLeave);
        dom.removeEventListener('pointerdown', onDown);
        dom.removeEventListener('pointerup', onUp);
      };

      engine.start();
      await nextFrames(engine, 1);
      for (const m of MODULES) {
        await m.install(need());
      }
      await warmUp(need(), p.x, p.z, start.height, () => globe?.warm() ?? Promise.resolve());
    },

    setSnapshot(snapshot: WorldSnapshot) {
      const c = need();
      c.data.setSnapshot(snapshot);
      globe?.invalidate();
      c.labels.setSnapshot(snapshot);
      const key = citySignature(snapshot);
      if (key !== cityKey) {
        cityKey = key;
        c.features.setCities(placeCities(snapshot.cities, opts.physical, c.shapes, snapshot.owner));
      }
      for (const m of MODULES) {
        m.setSnapshot?.(snapshot);
      }
    },

    setLens(lens: LensResult | null) {
      need().data.setLens(lens);
      globe?.invalidate();
      for (const m of MODULES) {
        m.setLens?.(lens);
      }
    },

    setMode(next: ViewMode) {
      mode = next;
      void mode;
      globe?.invalidate();
      for (const m of MODULES) {
        m.setMode?.(next);
      }
    },

    flyTo(hex: number, zoom?: number) {
      if (hex < 0 || hex >= HEX_COUNT) {
        return;
      }
      const c = hexCenter(hexCol(hex), hexRow(hex));
      need().camera.flyTo(c.x, c.z, zoom);
    },

    highlight(hexes: number[]) {
      need().data.setHighlight(hexes);
      globe?.invalidate();
    },

    onHover(cb) {
      hoverCbs.add(cb);
    },

    onClick(cb) {
      clickCbs.add(cb);
    },

    worldToScreen(hex: number) {
      const c = need();
      if (!picker) {
        return { x: 0, y: 0, visible: false };
      }
      if (globe && c.camera.globeAmount() >= GLOBE.grabFrom) {
        return globe.project(hex);
      }
      return picker.toScreen(hex, c.camera.pose().x);
    },

    stats(): ViewStats {
      const c = need();
      const s = c.engine.stats();
      return { fps: s.fps, drawCalls: s.drawCalls, triangles: s.triangles, backend: c.engine.backend };
    },

    setPose(pose: ViewPose) {
      const c = need();
      const target = pose.lon !== undefined && pose.lat !== undefined ? lonLatToWorld(pose.lon, pose.lat) : null;
      c.camera.jump({ x: target?.x, z: target?.z, height: pose.height, yaw: pose.yaw, pitch: pose.pitch });
    },

    async settle(timeoutMs = SETTLE_TIMEOUT_MS) {
      await settleCore(need(), timeoutMs);
    },

    setActors(actors: readonly ActorLabel[]) {
      const c = need();
      const colors: Array<{ primary: string; secondary: string } | null> = [];
      for (const a of actors) {
        colors[a.index] = a.colors;
      }
      c.data.setActorColors(colors);
      c.labels.setActors(actors);
    },

    markers() {
      // Labels plus every module's markers, so the harness truth gate sees all of them.
      const counts = need().labels.counts();
      const modules = MODULES.reduce((sum, m) => sum + (m.unsourced?.() ?? 0), 0);
      return { ...counts, unsourced: counts.unsourced + modules };
    },

    setTier(tier: QualityTier) {
      const c = need();
      c.engine.setTier(tier);
      globe?.setTier(tier);
      const s = c.engine.settings();
      c.lights.setShadows(s.shadows, s.shadowMapSize);
      c.terrain.setShadows(s.shadows);
      c.features.setShadows(s.shadows);
    },

    dispose() {
      detach?.();
      if (!core) {
        return;
      }
      for (const m of MODULES) {
        m.dispose?.();
      }
      core.terrain.dispose();
      core.features.dispose();
      core.labels.dispose();
      ocean?.dispose();
      globe?.dispose();
      core.data.dispose();
      core.engine.dispose();
      core = null;
      ocean = null;
      globe = null;
    },
  };
  return view;
}

/** Changes whenever the camera moves, so a still cursor is re-picked. */
function poseKey(p: CameraPose): string {
  return `${p.x},${p.z},${p.height},${p.yaw},${p.pitch}`;
}

/** The hex under a client pixel: through the sphere on the globe, else through the flat picker. */
function hexUnder(c: ViewCore, picker: Picker | null, clientX: number, clientY: number): number {
  if (c.camera.globeAmount() < GLOBE.grabFrom) {
    return picker ? picker.hexAt(clientX, clientY) : -1;
  }
  const at = c.camera.lonLatAt(clientX, clientY);
  return at ? hexAtLonLat(at.lon, at.lat) : -1;
}

/** Resolve once the camera has arrived and no terrain chunk is pending for a few frames. */
async function settleCore(c: ViewCore, timeoutMs: number): Promise<void> {
  const deadline = performance.now() + timeoutMs;
  let calm = 0;
  while (calm < SETTLE_FRAMES && performance.now() < deadline) {
    await nextFrames(c.engine, 1);
    const busy = !c.camera.settled() || c.terrain.stats().pending > 0;
    calm = busy ? 0 : calm + 1;
  }
}

/**
 * Compile every GPU pipeline before the first close zoom. Without this, the first zoom into
 * trees and peaks stalls the GPU for ~0.9 s while pipelines compile (RTX 3070 Ti, cold cache:
 * tools/perf/zoom-profile.mjs --fresh). Runs inside mount(), behind the app's loading card.
 */
async function warmUp(c: ViewCore, x: number, z: number, height: number, warmGlobe: () => Promise<void>): Promise<void> {
  const warm = lonLatToWorld(WARM_POSE.lon, WARM_POSE.lat);
  c.camera.jump({ x: warm.x, z: warm.z, height: WARM_POSE.height, pitch: WARM_POSE.pitch });
  await nextFrames(c.engine, WARM_FRAMES);

  // Async compile first (non-blocking pipeline creation), then real frames for the shadow passes.
  const endFeatureWarm = c.features.warm(warm.x, WARM_POSE.height / 10, warm.z);
  for (const m of MODULES) {
    m.warm?.(warm.x, WARM_POSE.height / 10, warm.z);
  }
  // The modules' warm-ups left their meshes visible: the globe bake compiles its variants of them too.
  try {
    await c.engine.compileAsync(warmGlobe);
    await nextFrames(c.engine, WARM_FRAMES);
  } finally {
    endFeatureWarm();
  }

  // The first globe frames compile the curl pipelines: do it here, not on the first zoom out.
  c.camera.jump({ x, z, height: GLOBE.to });
  await nextFrames(c.engine, WARM_FRAMES);

  // pitch null hands the tilt back to the automatic curve; the warm pose's 50° would otherwise
  // stay, tilting the start view past the map's height.
  c.camera.jump({ x, z, height, pitch: null });
  await settleCore(c, WARM_TIMEOUT_MS);
}

/** Resolve after `n` frames have rendered. */
function nextFrames(engine: Engine, n: number): Promise<void> {
  const target = engine.frames + n;
  return new Promise((resolve) => {
    const off = engine.afterFrame(() => {
      if (engine.frames >= target) {
        off();
        resolve();
      }
    });
  });
}
